import { z } from 'zod'

/**
 * PRD-024 §3.2 field types, slice 1 subset: the scalar types a request-bound form needs (text-free by design: the
 * only free text is the optional `note` field, at most one per form, off by default, capped at 500 characters).
 * Deferred to slice 2 with the file bindings: currency/branded Money, line groups, photo, signature, computed.
 *
 * `writes_to` (default chosen by Jarvis 2026-09-19, pending Sean): a field may name one target column from the
 * allowlist below. On accept (PRD-023 §3.8), the firm principal applies each bound value to its target under its own
 * RLS, as a `field_changed` event on the client. Nothing writes on submit; nothing writes to QuickBooks.
 */
export const FORM_FIELD_TYPES = ['short_text', 'integer', 'decimal', 'select', 'yes_no', 'date', 'email', 'phone', 'note'] as const
export type FormFieldType = typeof FORM_FIELD_TYPES[number]

/**
 * Where a bound answer may land (PRD-024 "bound to a target record"). Every target is a column the firm edits by hand
 * today (PRD-020 §3.1 client address, §3.3 contact, §3.4 file). Adding a target is a code change on purpose: the set
 * is the whole write surface a client user can reach, one accept step removed.
 */
export const WRITE_TARGETS = {
  'contact.name': { table: 'contact', column: 'name', types: ['short_text'] },
  'contact.organisation': { table: 'contact', column: 'organisation', types: ['short_text'] },
  'contact.email': { table: 'contact', column: 'email', types: ['email'] },
  'contact.phone': { table: 'contact', column: 'phone', types: ['phone', 'short_text'] },
  'client.address_line1': { table: 'client', column: 'address_line1', types: ['short_text'] },
  'client.address_line2': { table: 'client', column: 'address_line2', types: ['short_text'] },
  'client.address_city': { table: 'client', column: 'address_city', types: ['short_text'] },
  'client.address_state': { table: 'client', column: 'address_state', types: ['short_text', 'select'] },
  'client.address_postal_code': { table: 'client', column: 'address_postal_code', types: ['short_text'] },
  'client.fiscal_year_end_month': { table: 'client', column: 'fiscal_year_end_month', types: ['integer', 'select'] },
  'file.legal_name': { table: 'file', column: 'legal_name', types: ['short_text'] },
  'file.entity_type': { table: 'file', column: 'entity_type', types: ['short_text', 'select'] },
  'file.ein_last4': { table: 'file', column: 'ein_last4', types: ['short_text'] },
  'file.address_line1': { table: 'file', column: 'address_line1', types: ['short_text'] },
  'file.address_line2': { table: 'file', column: 'address_line2', types: ['short_text'] },
  'file.address_city': { table: 'file', column: 'address_city', types: ['short_text'] },
  'file.address_state': { table: 'file', column: 'address_state', types: ['short_text', 'select'] },
  'file.address_postal_code': { table: 'file', column: 'address_postal_code', types: ['short_text'] },
} as const satisfies Record<string, { table: 'contact' | 'client' | 'file', column: string, types: readonly FormFieldType[] }>
export type WriteTarget = keyof typeof WRITE_TARGETS
export const WRITE_TARGET_KEYS = Object.keys(WRITE_TARGETS) as WriteTarget[]

const Key = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'A field key is lower-case letters, digits and underscores, starting with a letter.')

/** An option key may be a bare number ("12" for December) so a select can write to an integer target. */
const OptionKey = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,39}$/, 'An option key is lower-case letters, digits, underscores and dashes.')

const Base = z.object({
  key: Key,
  label: z.string().trim().min(1).max(80),
  help: z.string().trim().max(200).optional(),
  required: z.boolean().default(false),
  writes_to: z.enum(WRITE_TARGET_KEYS as [WriteTarget, ...WriteTarget[]]).optional(),
})

export const FormField = z.discriminatedUnion('type', [
  Base.extend({ type: z.literal('short_text'), max_length: z.number().int().min(1).max(200).default(120) }),
  Base.extend({ type: z.literal('integer'), min: z.number().int().optional(), max: z.number().int().optional() }),
  Base.extend({ type: z.literal('decimal'), scale: z.number().int().min(0).max(4).default(2), min: z.number().optional(), max: z.number().optional() }),
  Base.extend({ type: z.literal('select'), options: z.array(z.object({ key: OptionKey, label: z.string().trim().min(1).max(80) })).min(2).max(50) }),
  Base.extend({ type: z.literal('yes_no') }),
  Base.extend({ type: z.literal('date') }),
  Base.extend({ type: z.literal('email') }),
  Base.extend({ type: z.literal('phone') }),
  Base.extend({ type: z.literal('note'), max_length: z.number().int().min(1).max(500).default(500) }),
])
export type FormField = z.infer<typeof FormField>

export class FormDefinitionError extends Error {
  readonly _tag = 'FormDefinitionError'
  constructor(readonly code: 'fields.invalid' | 'fields.duplicate_key' | 'fields.two_notes' | 'fields.writes_to_type' | 'fields.empty', message: string) {
    super(message)
  }
}

/** Parse and check an authored field list (PRD-024 §3.1 publish gate: named reasons, never a grayed button). */
export function parseFormFields(input: unknown): FormField[] {
  const parsed = z.array(FormField).min(1, 'A form needs at least one field.').max(60).safeParse(input)
  if (!parsed.success) {
    const first = parsed.error.issues[0]
    throw new FormDefinitionError(first?.message === 'A form needs at least one field.' ? 'fields.empty' : 'fields.invalid', first ? `${first.path.join('.') || 'fields'}: ${first.message}` : 'Fields are not valid.')
  }
  const fields = parsed.data
  const seen = new Set<string>()
  let notes = 0
  for (const f of fields) {
    if (seen.has(f.key))
      throw new FormDefinitionError('fields.duplicate_key', `Two fields share the key "${f.key}".`)
    seen.add(f.key)
    if (f.type === 'note' && ++notes > 1)
      throw new FormDefinitionError('fields.two_notes', 'One note field at most.')
    if (f.writes_to) {
      const target = WRITE_TARGETS[f.writes_to]
      if (!(target.types as readonly string[]).includes(f.type))
        throw new FormDefinitionError('fields.writes_to_type', `"${f.label}" writes to ${f.writes_to}, which takes ${target.types.join(' or ')}, not ${f.type}.`)
    }
  }
  return fields
}

export type FormValues = Record<string, string | number | boolean | null>

export interface FieldProblem { key: string, message: string }

/**
 * PRD-024 §3.4: validate a submission against the exact published fields; refuse whole with per-field reasons.
 * Returns the normalized values (trimmed strings, numbers as numbers, dates as YYYY-MM-DD, selects as option keys).
 */
export function validateSubmission(fields: ReadonlyArray<FormField>, raw: unknown): { ok: true, values: FormValues } | { ok: false, problems: FieldProblem[] } {
  const input = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>
  const problems: FieldProblem[] = []
  const values: FormValues = {}
  const known = new Set(fields.map(f => f.key))
  for (const k of Object.keys(input)) {
    if (!known.has(k))
      problems.push({ key: k, message: 'This field is not on the form.' })
  }
  for (const f of fields) {
    const v = input[f.key]
    const empty = v === undefined || v === null || (typeof v === 'string' && v.trim() === '')
    if (empty) {
      if (f.required)
        problems.push({ key: f.key, message: `${f.label} is required.` })
      else values[f.key] = null
      continue
    }
    switch (f.type) {
      case 'short_text':
      case 'note': {
        if (typeof v !== 'string') {
          problems.push({ key: f.key, message: `${f.label} must be text.` })
          break
        }
        const s = v.trim()
        if (s.length > f.max_length) {
          problems.push({ key: f.key, message: `${f.label} is over ${f.max_length} characters.` })
          break
        }
        values[f.key] = s
        break
      }
      case 'email': {
        const s = typeof v === 'string' ? v.trim() : ''
        if (!z.string().email().max(320).safeParse(s).success) {
          problems.push({ key: f.key, message: `${f.label} must be an email address.` })
          break
        }
        values[f.key] = s.toLowerCase()
        break
      }
      case 'phone': {
        const s = typeof v === 'string' ? v.trim() : ''
        if (!/^[+\d][\d\s().-]{5,39}$/.test(s)) {
          problems.push({ key: f.key, message: `${f.label} must be a phone number.` })
          break
        }
        values[f.key] = s
        break
      }
      case 'integer': {
        const n = typeof v === 'number' ? v : typeof v === 'string' && /^-?\d+$/.test(v.trim()) ? Number(v.trim()) : Number.NaN
        if (!Number.isInteger(n)) {
          problems.push({ key: f.key, message: `${f.label} must be a whole number.` })
          break
        }
        if ((f.min !== undefined && n < f.min) || (f.max !== undefined && n > f.max)) {
          problems.push({ key: f.key, message: `${f.label} must be between ${f.min ?? '−∞'} and ${f.max ?? '∞'}.` })
          break
        }
        values[f.key] = n
        break
      }
      case 'decimal': {
        const s = typeof v === 'number' ? String(v) : typeof v === 'string' ? v.trim() : ''
        if (!/^-?\d+(?:\.\d+)?$/.test(s)) {
          problems.push({ key: f.key, message: `${f.label} must be a number.` })
          break
        }
        const n = Number(s)
        const scale = (s.split('.')[1] ?? '').length
        if (scale > f.scale) {
          problems.push({ key: f.key, message: `${f.label} takes at most ${f.scale} decimal places.` })
          break
        }
        if ((f.min !== undefined && n < f.min) || (f.max !== undefined && n > f.max)) {
          problems.push({ key: f.key, message: `${f.label} must be between ${f.min ?? '−∞'} and ${f.max ?? '∞'}.` })
          break
        }
        values[f.key] = n
        break
      }
      case 'select': {
        const s = typeof v === 'string' ? v : ''
        if (!f.options.some(o => o.key === s)) {
          problems.push({ key: f.key, message: `${f.label}: choose one of the options.` })
          break
        }
        values[f.key] = s
        break
      }
      case 'yes_no': {
        const b = v === true || v === 'true' || v === 'yes' ? true : v === false || v === 'false' || v === 'no' ? false : null
        if (b === null) {
          problems.push({ key: f.key, message: `${f.label} must be yes or no.` })
          break
        }
        values[f.key] = b
        break
      }
      case 'date': {
        const s = typeof v === 'string' ? v.trim() : ''
        if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || Number.isNaN(Date.parse(`${s}T00:00:00Z`))) {
          problems.push({ key: f.key, message: `${f.label} must be a date (YYYY-MM-DD).` })
          break
        }
        values[f.key] = s
        break
      }
    }
  }
  return problems.length > 0 ? { ok: false, problems } : { ok: true, values }
}

/**
 * PRD-023 §3.6 / edge case 21: a note is refused when it looks like a secret (a run of 8+ characters with no spaces
 * mixing upper, lower and digits, or a well-known key prefix). Deliberately blunt; a false positive costs a rephrase.
 */
export function looksLikeSecret(note: string): boolean {
  if (/(?:^|\s)(?:sk|pk|rk)_(?:live|test)_[\w-]{8,}/i.test(note) || /AKIA[0-9A-Z]{12,}/.test(note) || /-----BEGIN [A-Z ]*PRIVATE KEY-----/.test(note))
    return true
  for (const token of note.split(/\s+/)) {
    if (token.length >= 8 && token.length <= 128 && /[a-z]/.test(token) && /[A-Z]/.test(token) && /\d/.test(token) && !/^[A-Z][a-z]+\d{1,4}$/.test(token) && !/^https?:\/\//i.test(token))
      return true
  }
  return false
}

/** The writes an accept applies: every bound field with a non-null value (PRD-023 §3.8 Accepted, PRD-024 slice 1). */
export function plannedWrites(fields: ReadonlyArray<FormField>, values: FormValues): Array<{ key: string, target: WriteTarget, table: 'contact' | 'client' | 'file', column: string, value: string | number | boolean }> {
  const out: Array<{ key: string, target: WriteTarget, table: 'contact' | 'client' | 'file', column: string, value: string | number | boolean }> = []
  for (const f of fields) {
    if (!f.writes_to)
      continue
    const v = values[f.key]
    if (v === null || v === undefined)
      continue
    const t = WRITE_TARGETS[f.writes_to]
    out.push({ key: f.key, target: f.writes_to, table: t.table, column: t.column, value: v })
  }
  return out
}
